import React, { Fragment, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery, useIsMutating, useMutation, useQuery } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import * as Yup from "yup"
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined"
import AddOutlinedIcon from "@mui/icons-material/AddOutlined"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { Card, CardContent, CardHeader, Grid, IconButton, useMediaQuery } from "@mui/material"
import {
  Button,
  InputText,
  Menu,
  MenuItem,
  NotificationUtils,
  Skeleton,
  Typography,
} from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { FormikProvider, useFormik } from "formik"
import { t } from "i18next"
import { capitalize } from "lodash"
import { v4 as uuidv4 } from "uuid"

import { Divider } from "../../../components/core/Divider"
import { BaseSimpleDialog } from "../../../components/dialogs/BaseSimpleDialog"
import { VersioningDialog } from "../../../components/dialogs/VersioningDialog"
import { VersionTag } from "../../../components/status/VersionTag"
import { getTheme } from "../../../hooks/UseTheme"
import { KonanAPI } from "../../../services/KonanAPI"
import { queryClient } from "../../../store/QueryClientStore"
import type { RootState } from "../../../store/ReduxStore"
import type { CreateWorkflowRequest } from "../../../types/custom/projects"
import type { CloneDataFilesRequest, Rule, VersionChangeRequest } from "../../../types/custom/rules"
import type { ConditionListFile } from "../../../types/generated/api/ConditionListFile"
import type { DataFileCloneResponse } from "../../../types/generated/api/DataFileCloneResponse"
import type { PaginatedScoreCardVersionsListList } from "../../../types/generated/api/PaginatedScoreCardVersionsListList"
import type { ScoreCardCreateUpdate } from "../../../types/generated/api/ScoreCardCreateUpdate"
import type { ScoreCardGroupRetrieve } from "../../../types/generated/api/ScoreCardGroupRetrieve"
import type { ScoreCardList } from "../../../types/generated/api/ScoreCardList"
import type { ScoreCardRetrieve } from "../../../types/generated/api/ScoreCardRetrieve"
import type { ScoreCardRuleCreateRequest } from "../../../types/generated/api/ScoreCardRuleCreateRequest"
import type { ScoreCardVersionsList } from "../../../types/generated/api/ScoreCardVersionsList"
import { WorkflowCreateRequest } from "../../../types/generated/api/WorkflowCreateRequest"
import { WorkflowInputFeature } from "../../../types/generated/api/WorkflowInputFeature"
import { useIsPermitted } from "../../../utils/PermissionsHelpers"
import { formatWorkflowComponent } from "../../../utils/WorkflowBlocksHelpers"
import { detectRuleType, getFieldErrorIfTouched, getNewRuleInfo, levelSchema } from "../../../utils/conditionHelpers"
import { extractScorecardErrorsMessages, getValidMaxMinorVersion } from "../../../utils/deploymentDetailsHelpers"
import { extractPageFromBackEndPaginationLink, getEnvVar } from "../../../utils/genericHelpers"
import { convertComplexConditionToComponentsGroupConditions, isTypeIncluded } from "../../../utils/rulesetHelpers"
import { updateComponentsDataFilesIds } from "../../ImportFlow/Helpers"
import { COMPONENT_TYPES, type SelectedComponent } from "../../Predictions/Interfaces"
import { ComponentTestingDialog } from "../../Predictions/components/ComponentTestingDialog"
import { EMPTY_SCORECARD_CONDITION } from "../../ScoreCardSets/Constants"
import { ScorecardLoadingComponent } from "../../ScoreCardSets/Scorecardset"
import type {
  CreateScoreCardRequest,
  GroupConditions,
  ScoreCardFormikValues,
  ScorecardProps,
  UpdateScorecardRequest,
} from "../Interfaces"
import { DefaultScore } from "./DefaultScore"
import { RuleCard } from "./RuleCard"

import styles from "./Scorecard.module.scss"

/**
 * ScorecardMenu component renders a menu for scorecard actions including deletion.
 *
 * @param name - The name of the scorecard.
 * @param uuid - The unique identifier of the scorecard.
 * @param {"card" | "table"} mode - The mode of the scorecard, either "card" or "table".
 * @returns A React element representing the scorecard menu.
 *
 * The component uses the following hooks:
 * - `useParams` to get the project ID from the URL parameters.
 * - `useState` to manage the state of the anchor element for the menu and the deletion dialog.
 * - `useMutation` to handle the deletion of the scorecard using an API call.
 *
 * The component renders:
 * - A deletion dialog (`BaseSimpleDialog`) that appears when the user opts to delete the scorecard.
 * - An icon button (`IconButton`) that opens the menu when clicked.
 * - A menu (`Menu`) with a menu item (`MenuItem`) to trigger the deletion dialog.
 * The deletion mutation:
 * - Invalidates relevant queries on success to refresh the data.
 * - Displays a success or error notification based on the outcome of the deletion.
 */

type ScorecardMenuProps = {
  name: string
  uuid: string
  mode: "Scorecard" | "ScoreTable"
  setIsTestDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
  canShowDetails: boolean
  canRemoveScorecard: boolean
}

export const ScorecardMenu = (props: ScorecardMenuProps): React.ReactElement => {
  const { name, uuid, mode, setIsTestDialogOpen, canRemoveScorecard, canShowDetails } = props
  const { id: projectId } = useParams<{ id: string }>()

  const theme = getTheme()

  const { t } = useTranslation("scorecards")

  const [anchor, setAnchor] = React.useState<null | HTMLElement>(null)
  const isMenuOpen = Boolean(anchor)

  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState<boolean>(false)

  // scorecard deletion Mutation
  const deleteScorecardMutation = useMutation<
    AxiosResponse,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosError<any>,
    { project_uuid: string; resource_uuid: string }
  >((props) => KonanAPI.deleteWorkflowResource({ ...props, resource_type: "scorecards" }), {
    onSuccess: async () => {
      await queryClient.invalidateQueries(["scorecards", projectId])

      NotificationUtils.toast(
        t("notifications.resourceDeleted", {
          resource: t(`menuItem.${mode === "Scorecard" ? "scorecard" : "scoreTable"}`),
        }),
        {
          severity: "success",
        },
      )
      setIsDeletionDialogOpen(false)
    },
    onError: ({ response }) => {
      NotificationUtils.toast(
        response?.data?.details ??
          t("notifications.resourceDeletionFailed", {
            resource: t(`menuItem.${mode === "Scorecard" ? "scorecard" : "scoreTable"}`),
          }),
        { severity: "error" },
      )
    },
  })

  return (
    <Fragment>
      {isDeletionDialogOpen && (
        <BaseSimpleDialog
          open
          name={name}
          isLoading={deleteScorecardMutation.isLoading}
          mode={"scorecard-deletion"}
          onAccept={() =>
            deleteScorecardMutation.mutateAsync({
              project_uuid: projectId as string,
              resource_uuid: uuid,
            })
          }
          onClose={() => setIsDeletionDialogOpen(false)}
        />
      )}

      <Grid container item xs display={"flex"} justifyContent={"center"} alignContent={"center"} mr={0.5} mt={0.25}>
        <IconButton
          aria-label="settings"
          size="small"
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchor(event.currentTarget)}
        >
          <MoreHorizIcon htmlColor={theme.palette.base.text[1]} />
        </IconButton>

        <Menu
          key={`deletion-menu-${uuid}`}
          anchorEl={anchor}
          open={isMenuOpen}
          onClose={() => setAnchor(null)}
          menuMaxContent
        >
          <MenuItem
            onClick={() => {
              setIsTestDialogOpen(true)
              setAnchor(null)
            }}
          >
            <Typography className={styles.optionsMenuItem} variant="a" color="base" variantColor={2}>
              {t("testBtn")}
            </Typography>
          </MenuItem>
          {canShowDetails && canRemoveScorecard && (
            <MenuItem
              onClick={() => {
                setIsDeletionDialogOpen(true)
                setAnchor(null)
              }}
            >
              <Typography variant="a" color="negative" variantColor={2}>
                {t("removeBtn")} {t(`menuItem.${mode === "Scorecard" ? "scorecard" : "scoreTable"}`)}
              </Typography>
            </MenuItem>
          )}
        </Menu>
      </Grid>
    </Fragment>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const groupSchema: Yup.AnyObjectSchema = Yup.object().shape({
  andOr: Yup.string().required(),
  conditions: Yup.array().of(levelSchema),
  groups: Yup.array().of(Yup.lazy(() => groupSchema) as unknown as Yup.AnyObjectSchema),
})

// Yup validation schema
// eslint-disable-next-line react-refresh/only-export-components
export const validationSchema = Yup.object({
  name: Yup.string().required(t("validation.scorecardNameRequired")),
  weight: Yup.number().typeError(t("validation.numericalValueRequired")),
  cards: Yup.array().of(
    Yup.object().shape({
      conditions: Yup.array().of(levelSchema),
      groups: Yup.array().of(groupSchema),
      return_value: Yup.number()
        .typeError(t("validation.numericValueRequired"))
        .required(t("validation.ruleWeightRequired")),
    }),
  ),
})

/**
 * Renders the Scorecard component.
 *
 * @param {Readonly<ScorecardProps>} props - The props for the Scorecard component.
 * @returns {React.ReactElement} The rendered Scorecard component.
 */
export function Scorecard(props: Readonly<ScorecardProps>): React.ReactElement {
  const {
    variant,
    uuid,
    scorecard,
    isAllComponentsLoading,
    isCreateMode = false,
    handleCancel,
    goToFirstPage,
    disableDuplicateButton,
    duplicateScoreCard,
    removeComponent,
    setTotalNumOfCurrentProjComponents,
    setCompletedCardsCounter,
    setComponentsErrorMsgs,
    isPermittedToCloneFiles,
    allComponentsDataRef,
    clonedFilesIdsMap,
    isAudit = false,
  } = props

  const { id: projectId } = useParams<{ id: string }>()

  const workflowsList = useSelector((state: RootState) => state?.workflow?.workflows)

  const theme = getTheme()
  const { ref: lastVersionRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isScorecardVersionsListLoading && !isFetchingNextPage && fetchNextPage()
    },
  })

  const { t } = useTranslation("scorecards")

  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canShowDetails = useIsPermitted("Retrieve scorecard", permissions.konan, flattenedKonanPermissions)
  const canRemoveScorecard = useIsPermitted("Delete scorecard", permissions.konan, flattenedKonanPermissions)

  const smallActionButtons = useMediaQuery("(min-width:925px)")

  const maxNumberOfRules = Number(getEnvVar("KONAN_MAX_RULE_NUMBER"))

  const [mode, setMode] = useState<"create" | "edit" | "read">(isCreateMode ? "create" : "read")
  const [anchorEl, setAnchorEl] = useState<undefined | HTMLElement>()

  // specific media query breakpoint
  const [isVersionDialogOpen, setIsVersionDialogOpen] = useState<boolean>(false)
  const [isTestDialogOpen, setIsTestDialogOpen] = useState<boolean>(false)
  const [selectedVersion, setSelectedVersion] = useState<string | undefined>(undefined)

  // Stores the latest minor version for each major version to determine valid restoration points
  // TODO: Move this state into a separate versioning dialog component along with version related logic
  // for better separation of concerns
  const [maxMinorVersionsMap, setMaxMinorVersionsMap] = useState<Record<string, string>>({})

  // we need this hook to get the mutation loading state from the react query store
  // because the component keeps unmounting and mounting so until we refactor this part we can not depend on the isLoading
  // property on the mutation because when the component unmount and mounts again we lose the isLoading state
  const isMutating = useIsMutating({
    predicate: (mutation) =>
      mutation.options.mutationKey === "filesClone" + uuid ||
      mutation.options.mutationKey === "componentCreate" + uuid ||
      mutation.options.mutationKey === "workFlowCreate" + uuid,
  })

  /**
   * Fetches the scorecard data from the server.
   *
   * @returns The scorecard data and loading status.
   */
  const { data: scorecardData, isLoading: isScorecardDataLoading } = useQuery<
    AxiosResponse<ScoreCardGroupRetrieve>,
    AxiosError
  >(["scorecard", scorecard?.uuid], () => KonanAPI.fetchScorecard(projectId as string, scorecard?.uuid as string), {
    onSuccess: async ({ data }) => {
      const activeVersion = data?.active_version

      await formik.setValues({
        uuid: data.uuid,
        name: scorecard?.name ?? data.name,
        format: (activeVersion?.format?.toLowerCase() as string) || "list",
        defaultScore: (activeVersion?.default_score as string) || "0",
        cards:
          activeVersion?.rules
            ?.sort((a, b) => (a.order && b.order ? a?.order - b?.order : 0))
            .map((rule) => {
              const { conditions, groups } = convertComplexConditionToComponentsGroupConditions(
                rule?.condition,
                "Scorecard",
              )

              return {
                id: uuidv4(),
                name: rule?.name,
                type: detectRuleType(rule?.condition),
                conditions: conditions ? conditions : [],
                groups: groups ? groups : [],
                return_value: rule?.return_value,
                condition_list_files: rule?.condition_list_files,
                schemaFeatureType: WorkflowInputFeature.type.TEXT,
              }
            }) || [],
      })
    },
    // Only enable the query if the scorecard?.uuid is not undefined
    enabled: canShowDetails && !!scorecard?.uuid && variant !== "import",
  })

  const {
    isLoading: isScorecardVersionsListLoading,
    isFetchingNextPage,
    data: scorecardVersionsList,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedScoreCardVersionsListList>, AxiosError, ScoreCardVersionsList>(
    ["scorecardVersionsList", scorecard?.uuid],
    ({ pageParam = 1 }) =>
      KonanAPI.listScorecardVersions({
        projectId: projectId as string,
        scorecardId: scorecard?.uuid as string,
        page_size: 10,
        page: pageParam,
      }),
    {
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce<ScoreCardVersionsList[]>(
          (accumulator, page) => accumulator.concat(page.data.results as ScoreCardVersionsList[]),
          [],
        ),
      }),
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: !!projectId && !!scorecard?.uuid && isVersionDialogOpen && variant !== "import",
      refetchOnMount: true,
    },
  )

  // fetch Version
  const { data: scorecardVersionData, isLoading: isScorecardVersionDataLoading } = useQuery<
    AxiosResponse<ScoreCardRetrieve>,
    AxiosError
  >(
    ["selectedScorecardVersion", scorecard?.uuid, selectedVersion],
    () =>
      KonanAPI.retrieveScorecardVersion({
        projectId: projectId as string,
        scorecardId: scorecard?.uuid as string,
        version: selectedVersion as string,
      }),
    {
      onSuccess: async () => {
        if (
          selectedVersion &&
          !maxMinorVersionsMap[selectedVersion?.split(".")?.[0] as string] &&
          scorecardVersionsList?.pages
        ) {
          const result = getValidMaxMinorVersion(scorecardVersionsList?.pages, selectedVersion)
          setMaxMinorVersionsMap((prevResults) => ({
            ...prevResults,
            ...result,
          }))
        }
      },
      enabled: !!projectId && !!scorecard?.uuid && isVersionDialogOpen && !!selectedVersion,
    },
  )

  const createScoreCardMutation = useMutation<
    AxiosResponse,
    AxiosError<CreateScoreCardRequest>,
    CreateScoreCardRequest
  >(KonanAPI.createScorecard, {
    mutationKey: "componentCreate" + uuid,
    onSuccess: async () => {
      await queryClient.invalidateQueries(["scorecards", projectId])

      // closing the scorecard view upon creation
      handleCancel?.()
      NotificationUtils.toast(t("notifications.scorecardCreated"), {
        severity: "success",
      })

      goToFirstPage?.()
    },
    onError: (error: AxiosError<CreateScoreCardRequest>) => {
      if (variant === "import" && uuid) {
        const formattedErrorMessage =
          capitalize(error?.response?.data?.name?.[0]) || t("notifications.scorecardCreateError")

        setComponentsErrorMsgs?.((prev) => ({ ...prev, [uuid]: formattedErrorMessage }))
        return
      }

      const errorMessage = extractScorecardErrorsMessages(error?.response?.data, "create")

      NotificationUtils.toast(errorMessage, {
        severity: "error",
      })
    },
  })

  const updateScorecardMutation = useMutation<
    AxiosResponse<ScoreCardCreateUpdate>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosError<any>,
    UpdateScorecardRequest
  >(KonanAPI.updateScorecard, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(["scorecards", projectId])
      await queryClient.invalidateQueries(["scorecard", scorecard?.uuid])

      setMode("read")

      NotificationUtils.toast(t("notifications.scorecardUpdated"), {
        severity: "success",
      })
    },
    onError: (response: AxiosError<UpdateScorecardRequest>) => {
      const errorMessage = extractScorecardErrorsMessages(response?.response?.data, "update")

      NotificationUtils.toast(errorMessage, {
        severity: "error",
      })
    },
  })

  const cloneDataFilesMutation = useMutation<AxiosResponse<DataFileCloneResponse[]>, AxiosError, CloneDataFilesRequest>(
    KonanAPI.cloneDataFiles,
    {
      mutationKey: "filesClone" + uuid,
      onSuccess: async ({ data }) => {
        if (!clonedFilesIdsMap) return

        data.forEach((file) => {
          clonedFilesIdsMap[file.source.uuid] = file.uuid
        })

        await formik.setValues((prev) => {
          const updatedComponent = updateComponentsDataFilesIds(prev, clonedFilesIdsMap) as ScoreCardFormikValues

          return { ...updatedComponent }
        })
      },
    },
  )

  // create workflow mutation
  const createWorkflowMutation = useMutation<AxiosResponse, AxiosError, CreateWorkflowRequest>(
    KonanAPI.createWorkflow,
    {
      mutationKey: "workFlowCreate" + uuid,
      onSuccess: async () => {
        await queryClient.invalidateQueries(["workflows", projectId])
        await queryClient.invalidateQueries(["project", projectId])
      },
    },
  )

  /**
   * Mutation hook for changing the version of a resource.
   *
   * @param {VersionChangeRequest} requestData - The request data for changing the version.
   * @returns {void}
   */
  const versionSwitchMutation = useMutation<AxiosResponse, AxiosError, VersionChangeRequest>(
    KonanAPI.changeResourceVersion,
  )

  const formikInitialValues = (uuid && allComponentsDataRef?.current[uuid]) || {
    uuid: "",
    name: scorecard?.name ?? "",
    format: "list",
    defaultScore: "0",
    cards: [
      {
        id: `rule-${uuidv4()}`,
        name: "",
        type: "simple" as "simple" | "complex",
        conditions: [EMPTY_SCORECARD_CONDITION] as Rule[],
        groups: [] as GroupConditions[],
        return_value: "",
        condition_list_files: [] as ConditionListFile[],
        schemaFeatureType: WorkflowInputFeature.type.TEXT,
      },
    ],
  }

  // simpler formik specific for editing the current scorecard
  const formik = useFormik({
    validationSchema: validationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    initialValues: formikInitialValues,
    onSubmit: async ({ name, cards }) => {
      const { trimmedName, featuresArray, finalComponentFormat } = formatWorkflowComponent({
        name,
        cards,
      })

      // value is split into two parts: before and after the decimal point (if any) to validate the length of each part
      // This is because the backend only accepts 6 digits before the decimal point and 2 digits after
      if (typeof formik.values.defaultScore === "string") {
        const splitNewValue = formik.values.defaultScore.split(".")

        if (splitNewValue[0] && splitNewValue[0].length > 6)
          return NotificationUtils.toast(t("notifications.defaultScoreDigitsBeforeError"), {
            severity: "error",
          })

        if (splitNewValue[1] && splitNewValue[1].length > 2)
          return NotificationUtils.toast(t("notifications.defaultScoreDecimalPlacesError"), {
            severity: "error",
          })
      }

      if (workflowsList?.length === 0 && featuresArray?.length > 0) {
        await createWorkflowMutation.mutateAsync({
          name: "Untitled-1",
          state: WorkflowCreateRequest.state.DISABLED,
          projectId: projectId as string,
          schema: [...featuresArray],
        })
      }

      if (mode === "create" || variant === "import") {
        await createScoreCardMutation.mutateAsync({
          // Pass the data of the current Scorecard to the mutation
          name: trimmedName,
          projectId: projectId as string,
          default_score: formik.values.defaultScore,
          rules: [...(finalComponentFormat as unknown as ScoreCardRuleCreateRequest[])],
        })

        if (!uuid) return

        removeComponent?.(uuid)
        setCompletedCardsCounter?.((prev) => ++prev)
      } else {
        await updateScorecardMutation.mutateAsync({
          // Pass the data of the current Scorecard to the mutation
          name: trimmedName === scorecard?.name ? undefined : trimmedName,
          scorecardId: scorecard?.uuid as string,
          projectId: projectId as string,
          default_score: formik.values.defaultScore,
          rules: [...(finalComponentFormat as unknown as ScoreCardRuleCreateRequest[])],
        })
      }
    },
  })
  // This Effect to sync the formik changes with the components ref
  // to submit changes with Save all button
  useEffect(() => {
    if (!allComponentsDataRef || !uuid) return

    allComponentsDataRef.current[uuid] = formik.values
  }, [formik.values, uuid, allComponentsDataRef])

  // handler for activating scorecard version
  const changeActiveScorecardVersion = async (version: string): Promise<void> => {
    try {
      await versionSwitchMutation.mutateAsync({
        projectId: projectId as string,
        resourceId: scorecard?.uuid as string,
        version,
        resource: "scorecards",
      })

      NotificationUtils.toast(
        t("versionRestored", {
          version: version,
        }),
        {
          severity: "success",
        },
      )

      await queryClient.invalidateQueries(["scorecard", scorecard?.uuid])
      await queryClient.invalidateQueries(["scorecardVersionsList", scorecard?.uuid])
      // TODO: remove this line when backend order scorecards based on weight
      /**
       * currently, the backend returns the order of scorecards, according to the created_at field
       * which changes after each new version, so let's say this is the order of scorecards (a - b - c)
       * after updating b for example, it will be -> (b - a - c) .. so this shift in order will reflect
       * in the content of the version history because now the same index is pointing to a different scorecard
       * so as of right now, after restoring the newly version we automatically close the dialog
       */
    } catch (error) {
      NotificationUtils.toast(t("notifications.scorecardsetsRestoreVersionError"), {
        severity: "error",
      })
    }
  }

  // children component for the versioning dialog for scorecard
  const ScorecardContainerInVersionHistory = (): React.ReactElement => {
    // extracting the current major version
    const majorSelectedVersion = (selectedVersion ?? scorecardData?.data.active_version?.version)?.split(".")?.[0]

    // show restore button when the current major version from the selected version/active version existed in our valid versions hashMap
    // and it's not the current active version
    const shouldShowRestoreButton =
      maxMinorVersionsMap[majorSelectedVersion as string] ===
        (selectedVersion ?? scorecardData?.data.active_version?.version) &&
      maxMinorVersionsMap[majorSelectedVersion as string] !== scorecardData?.data.active_version?.version

    const tagVariant = scorecardData?.data.active_version?.version === selectedVersion ? "green" : "blue"

    // simpler formik specific for editing the current scorecard
    const versionFormik = useFormik({
      initialValues: {
        uuid: uuidv4(),
        name: formik.values.name,
        defaultScore: scorecardVersionData?.data?.default_score,
        cards: scorecardVersionData?.data?.rules?.map((rule) => {
          const { conditions, groups } = convertComplexConditionToComponentsGroupConditions(
            rule?.condition,
            "Scorecard",
          )

          return {
            id: rule.uuid,
            name: rule.name,
            type: detectRuleType(rule?.condition),
            condition: rule?.condition,
            conditions: conditions ? conditions : [],
            groups: groups ? groups : [],
            condition_list_files: rule.condition_list_files,
            return_value: rule.return_value,
            schemaFeatureType: WorkflowInputFeature.type.TEXT,
          }
        }),
      },
      onSubmit: () => {
        return
      },
    })

    if (isScorecardVersionsListLoading || isScorecardVersionDataLoading) {
      return (
        <Grid container xs={12} gap={1} direction={"column"}>
          <Grid item className={"versioning-dialog-header"}>
            <Skeleton height={30} width={300} />
          </Grid>

          <Grid item px={2}>
            <ScorecardLoadingComponent />
          </Grid>
        </Grid>
      )
    }

    return (
      <Grid container>
        {/* Header */}
        <Grid
          item
          xs={12}
          alignItems="center"
          display="flex"
          justifyContent="space-between"
          className={"versioning-dialog-header"}
        >
          <Grid item gap={1} xs={9} display="flex">
            <Typography variant="h2-bold" style={{ textTransform: "inherit", marginRight: "5px" }}>
              {scorecardData?.data?.name}
            </Typography>

            <VersionTag
              version={
                isNaN(Number(scorecardVersionData?.data?.version))
                  ? undefined
                  : (Number(scorecardVersionData?.data?.version) ?? selectedVersion)
              }
              color={scorecardVersionData?.data ? tagVariant : "green"}
            />
          </Grid>

          {shouldShowRestoreButton && (
            <Button
              variant="secondary"
              size="small"
              onClick={() => changeActiveScorecardVersion(selectedVersion as string)}
              disabled={versionSwitchMutation.isLoading}
              isLoading={versionSwitchMutation.isLoading}
            >
              {t("restoreBtn")}
            </Button>
          )}
        </Grid>

        {/* Body */}
        <Grid item xs={12} p={1.5} pt={0} mt={1}>
          <FormikProvider value={versionFormik}>
            {versionFormik?.values.cards?.map((card, index) => {
              return (
                <RuleCard
                  key={card.id}
                  isScorecardLoading={Boolean(updateScorecardMutation.isLoading)}
                  id={`${index}`}
                  scorecardIndex={index}
                  ruleIndex={index}
                  updateMode={false}
                  createMode={false}
                />
              )
            })}
          </FormikProvider>

          <Grid item px={1.8}>
            <Divider mt={1} mb={2} />

            <DefaultScore value={versionFormik?.values.defaultScore ?? "0"} isReadMode={true} isDisabled={true} />
          </Grid>
        </Grid>
      </Grid>
    )
  }

  const handleAddingNewRule = (ruleType: "simple" | "complex"): void => {
    const condition = getNewRuleInfo(formik?.values)?.commonFeature

    formik?.setFieldValue(`cards`, [
      ...formik?.values?.cards,
      {
        type: ruleType,
        id: `new-${uuidv4()}`,
        name: `new-${uuidv4()}`,
        conditions:
          ruleType === "complex"
            ? Array(2).fill({
                ...EMPTY_SCORECARD_CONDITION,
                feature: condition?.name,
                type: condition?.type,
                value: isTypeIncluded(condition?.type as string) ? condition?.type : "",
              })
            : [
                {
                  ...EMPTY_SCORECARD_CONDITION,
                  feature: condition?.name,
                  type: condition?.type,
                  value: isTypeIncluded(condition?.type as string) ? condition?.type : "",
                },
              ],
        groups: [],
        return_value: "",
        condition_list_files: [],

        schemaFeatureType: WorkflowInputFeature.type.TEXT,
      },
    ])
  }

  // handle clicking the save button for one scoreCard
  const saveImportedScorecardHandler = async (): Promise<void> => {
    if (!uuid || !clonedFilesIdsMap) return

    setComponentsErrorMsgs?.((prev) => {
      delete prev?.[uuid]
      return { ...prev }
    })

    if (allComponentsDataRef?.current[uuid]?.name.trim() === "") {
      setComponentsErrorMsgs?.((prev) => ({ ...prev, [uuid]: t("notifications.scorecardNameRquired") }))
      return
    }

    const cardsFilesIds = new Set<string>()

    const clonedFilesIds = Object.values(clonedFilesIdsMap)

    for (const card of formik.values.cards) {
      if (card.condition_list_files.length > 0) {
        card.condition_list_files.forEach((file) => {
          if (file.uuid && !clonedFilesIds.includes(file.uuid))
            cardsFilesIds.add(JSON.stringify({ uuid: file.uuid, version: file.version }))
        })
      }
    }

    if (cardsFilesIds.size > 0) {
      await cloneDataFilesMutation.mutateAsync({
        project_uuid: projectId as string,
        data_files: Array.from(cardsFilesIds).map((file) => JSON.parse(file)),
      })
    }

    await formik.submitForm()
  }

  const handleCancelScorecard = (): void => {
    if (isCreateMode) {
      handleCancel?.()

      return
    }

    setMode("read")

    formik.setValues({
      uuid: scorecardData?.data?.uuid || "",
      name: scorecardData?.data?.name || "",
      format: "list",
      defaultScore: (scorecardData?.data.active_version?.default_score as string) || "0",
      cards:
        scorecardData?.data.active_version?.rules
          ?.sort((a, b) => (a?.order && b?.order ? a.order - b.order : 0))
          .map((rule) => {
            const { conditions, groups } = convertComplexConditionToComponentsGroupConditions(
              rule?.condition,
              "Scorecard",
            )

            return {
              type: detectRuleType(rule?.condition),
              id: uuidv4(),
              name: rule?.name,
              conditions: conditions ? conditions : [],
              groups: groups ? groups : [],
              return_value: rule?.return_value,
              condition_list_files: rule?.condition_list_files,
              condition: rule?.condition,
              schemaFeatureType: WorkflowInputFeature.type.TEXT,
            }
          }) || [],
    })
  }

  const isActionBtnsDisabled = !!isMutating || isAllComponentsLoading

  const selectedScorecard: SelectedComponent = useMemo(
    () => ({
      id: scorecard?.uuid ?? "",
      type: COMPONENT_TYPES.SCORECARD,
      data: scorecard as ScoreCardList,
    }),
    [scorecard],
  )

  return (
    <Fragment>
      {/* Testing Dialog */}
      {isTestDialogOpen && (
        <ComponentTestingDialog
          isOpen={isTestDialogOpen}
          handleClose={() => setIsTestDialogOpen(false)}
          selectedComponent={selectedScorecard}
        />
      )}
      {/* Versioning Dialog */}
      {isVersionDialogOpen && scorecardData?.data.active_version && (
        <VersioningDialog
          activeVersion={scorecardData?.data.active_version}
          setSelectedVersion={setSelectedVersion}
          selectedVersion={selectedVersion}
          isOpen={isVersionDialogOpen}
          versions={scorecardVersionsList?.pages || []}
          onClose={() => setIsVersionDialogOpen(false)}
          infiniteScrollProps={{
            hasNextPage,
            isFetchingNextPage,
            lastVersionRef,
          }}
          maxWidth="lg"
        >
          <ScorecardContainerInVersionHistory />
        </VersioningDialog>
      )}

      {isScorecardDataLoading ? (
        <Grid container direction="column" justifyContent="flex-start" alignItems="center" spacing={2}>
          <ScorecardLoadingComponent />
        </Grid>
      ) : (
        <Card className={styles.card}>
          <CardHeader
            className={styles.cardHeader}
            disableTypography
            title={
              <Grid
                direction={smallActionButtons ? "row" : "column"}
                container
                justifyContent={smallActionButtons ? "space-between" : "flex-start"}
                alignItems={smallActionButtons ? "center" : "flex-start"}
                maxWidth="100%"
              >
                {mode !== "read" || (variant === "import" && !isActionBtnsDisabled) ? (
                  <Grid item container width="fit-content" xs={6} md={3}>
                    <InputText
                      id={`name`}
                      hideDescription
                      placeholder={t("inputNamePlaceholder")}
                      value={formik.values.name}
                      handleChange={formik.handleChange}
                      handleBlur={formik.handleBlur}
                      error={getFieldErrorIfTouched(formik?.errors, formik?.touched, `name`)}
                      disabled={formik.isSubmitting || Boolean(updateScorecardMutation.isLoading)}
                      fullWidth
                    />
                  </Grid>
                ) : (
                  <Grid item container width="fit-content" xs={6}>
                    <Typography style={{ width: "100%" }} className={styles.cardTitle} noWrap variant="h3-bold">
                      {formik.values.name}
                    </Typography>
                  </Grid>
                )}

                {canShowDetails && (
                  <Grid
                    item
                    container
                    xs={12}
                    sm={6}
                    justifyContent="flex-end"
                    style={{ justifyContent: smallActionButtons ? "flex-end" : "flex-start" }}
                    gap={1}
                  >
                    {mode === "read" && variant !== "import" ? (
                      <Fragment>
                        {!isAudit && (
                          <Fragment>
                            {mode === "read" && (
                              <Button
                                size="small"
                                variant="secondary"
                                disabled={disableDuplicateButton}
                                onClick={() => {
                                  duplicateScoreCard?.(scorecard as ScoreCardList)
                                }}
                                alignSelf="center"
                              >
                                {t("duplicateBtn")}
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="secondary"
                              onClick={() => {
                                setSelectedVersion(scorecardData?.data.active_version?.version)
                                setIsVersionDialogOpen(true)
                              }}
                            >
                              {t("historyBtn")}
                            </Button>
                          </Fragment>
                        )}
                        <Button
                          size="small"
                          variant="secondary"
                          disabled={Boolean(updateScorecardMutation.isLoading)}
                          onClick={() => setMode("edit")}
                        >
                          {t("editBtn")}
                        </Button>
                      </Fragment>
                    ) : variant === "import" ? (
                      <Fragment>
                        <Button
                          variant="secondary"
                          size="small"
                          disabled={isActionBtnsDisabled}
                          onClick={() => {
                            if (!uuid) return
                            removeComponent?.(uuid)
                            setTotalNumOfCurrentProjComponents?.((prev) => --prev)
                          }}
                          tooltipPlacement="top"
                          tooltip={t("dismissBtnTooltip")}
                        >
                          {t("dismissBtn")}
                        </Button>
                        <Button
                          variant="primary"
                          size="small"
                          disabled={isActionBtnsDisabled || !isPermittedToCloneFiles}
                          onClick={saveImportedScorecardHandler}
                          isLoading={isActionBtnsDisabled}
                        >
                          {t("saveBtn")}
                        </Button>
                      </Fragment>
                    ) : (
                      <Fragment>
                        <Button
                          disabled={Boolean(updateScorecardMutation.isLoading)}
                          onClick={handleCancelScorecard}
                          size="small"
                          variant={"secondary"}
                        >
                          {t("cancelBtn")}
                        </Button>
                        <Button
                          variant="primary"
                          size="small"
                          onClick={() => formik.submitForm()}
                          disabled={Boolean(updateScorecardMutation.isLoading)}
                          isLoading={updateScorecardMutation.isLoading || createScoreCardMutation.isLoading}
                        >
                          {t("saveBtn")}
                        </Button>
                      </Fragment>
                    )}
                  </Grid>
                )}
              </Grid>
            }
            action={
              mode === "read" &&
              variant !== "import" &&
              !isAudit && (
                <ScorecardMenu
                  name={formik.values.name}
                  uuid={scorecard?.uuid as string}
                  mode="Scorecard"
                  setIsTestDialogOpen={setIsTestDialogOpen}
                  canShowDetails={canShowDetails}
                  canRemoveScorecard={canRemoveScorecard}
                />
              )
            }
          />

          {/* Card content */}
          <CardContent className={styles.cardContent}>
            {canShowDetails ? (
              <Grid py={0.5} item container xs={12} direction="column" justifyContent="center">
                <FormikProvider value={formik}>
                  {formik?.values?.cards?.map((item, index: number) => (
                    <RuleCard
                      key={item.id}
                      isScorecardLoading={updateScorecardMutation.isLoading}
                      ruleIndex={index}
                      editMode={mode === "edit"}
                      createMode={mode === "create"}
                    />
                  ))}
                </FormikProvider>

                {mode !== "read" && (
                  <Grid item mb={1} ml={2}>
                    <Button
                      size="regular"
                      variant="ghost"
                      disabled={
                        formik?.isSubmitting ||
                        Boolean(updateScorecardMutation.isLoading) ||
                        formik.values.cards?.length > maxNumberOfRules
                      }
                      onClick={(event) => setAnchorEl(event?.currentTarget)}
                      tooltip={formik.values.cards?.length > maxNumberOfRules ? t("maxNumofRulesTooltip") : undefined}
                    >
                      + {t("addRuleBtn")}
                    </Button>
                    <Menu
                      key="basic-menu1"
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={() => setAnchorEl(undefined)}
                      menuMaxContent
                    >
                      <MenuItem
                        onClick={() => {
                          handleAddingNewRule("simple")
                          setAnchorEl(undefined)
                        }}
                      >
                        <Grid container display={"flex"} gap={1} height={"20px"}>
                          <Grid item>
                            <AddOutlinedIcon fontSize="small" htmlColor={theme.palette.base.text[1]} />
                          </Grid>
                          <Grid item>
                            <Typography variant="a"> {t("addRuleBtn")}</Typography>
                          </Grid>
                        </Grid>
                      </MenuItem>

                      <MenuItem
                        onClick={() => {
                          handleAddingNewRule("complex")
                          setAnchorEl(undefined)
                        }}
                      >
                        <Grid container display={"flex"} gap={1} height={"20px"}>
                          <Grid item>
                            <AddBoxOutlinedIcon fontSize="small" htmlColor={theme.palette.base.text[1]} />
                          </Grid>
                          <Grid item>
                            <Typography variant="a">{t("addGroupBtn")}</Typography>
                          </Grid>
                        </Grid>
                      </MenuItem>
                    </Menu>
                  </Grid>
                )}

                <Grid item px={1.8} pb={1.5}>
                  <Divider mt={1} mb={2} />

                  <DefaultScore
                    value={formik.values.defaultScore ?? "0"}
                    setValue={(value: string) => formik.setFieldValue("defaultScore", value)}
                    isReadMode={mode === "read"}
                    isDisabled={updateScorecardMutation.isLoading || createScoreCardMutation.isLoading}
                  />
                </Grid>
              </Grid>
            ) : (
              <Grid
                container
                padding={2}
                margin={2}
                border={1}
                borderColor={"var(--base-border-1)"}
                bgcolor={"var(--base-background-1)"}
                borderRadius={1}
              >
                <Typography variant="label-regular" color="base">
                  {t("hiddenDuePermission")}
                </Typography>
              </Grid>
            )}
          </CardContent>
        </Card>
      )}
    </Fragment>
  )
}
